import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Users, Contact, Building2, MapPin, UserCheck } from 'lucide-react';
import { CountryFilter } from '@/components/CountryFilter';

import UsersAdminPage from './UsersAdminPage';
import KeyContactsTab from './KeyContactsTab';
import ClubsPage from './ClubsPage';
import VenuesPage from './VenuesPage';

/**
 * People & Places — one-stop admin console for user administration,
 * key contacts, skating organisations and venues (Feb 2026).
 *
 * The four content tabs re-use the existing standalone pages/components
 * in "embedded" mode so their behaviour stays identical. A fifth
 * pseudo-tab links out to the Skaters & Entries manager — clicking it
 * navigates rather than swapping in-page content.
 *
 * Layout mirrors ContentLibraryPage: page title & tagline up top, tab
 * bar underneath, each tab body carries its own H2 sub-header.
 */
const TABS = [
  { id: 'users',    label: 'User Administration',    icon: Users },
  { id: 'contacts', label: 'Key Contacts',           icon: Contact },
  { id: 'orgs',     label: 'Skating Organisations',  icon: Building2 },
  { id: 'venues',   label: 'Venues',                 icon: MapPin },
  { id: 'skaters',  label: 'Skaters & Entries',      icon: UserCheck, link: '/skaters-entries' },
];

const DEFAULT_TAB = 'users';

export default function PeoplePlacesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentUser, setCurrentUser] = useState(null);
  const [supportData, setSupportData] = useState({ clubs: [], venues: [] });
  // Shared country filter — mirrors the "Select Competition" pattern
  // used on the Documents page so all four tabs are filtered by a
  // single control instead of one per tab.
  const [country, setCountry] = useState('');

  // Tab picked via ?tab=… when set (deep-link friendly); otherwise
  // "users" for supers, "contacts" for standard users.
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    TABS.find(t => t.id === initialTab) ? initialTab : DEFAULT_TAB
  );

  // Optional ?action=create — the Dashboard Quick Actions use this to
  // land on the venues / orgs tab with the New … dialog already open.
  const [autoOpenCreate, setAutoOpenCreate] = useState(
    searchParams.get('action') === 'create'
  );
  const consumeAutoOpen = () => {
    setAutoOpenCreate(false);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setCurrentUser(r.data))
      .catch(() => {});
  }, []);

  // Standard users don't have access to the User Administration tab —
  // slide them to Key Contacts once we know their role.
  useEffect(() => {
    if (currentUser && currentUser.role !== 'super' && activeTab === 'users') {
      setActiveTab('contacts');
    }
  }, [currentUser, activeTab]);

  // KeyContactsTab expects clubs + venues props for its dropdowns —
  // fetch them once at the parent level so all tabs share the data.
  useEffect(() => {
    axios.get(`${API}/clubs`, { withCredentials: true })
      .then(r => setSupportData(prev => ({ ...prev, clubs: r.data || [] })))
      .catch(() => {});
    axios.get(`${API}/venues`, { withCredentials: true })
      .then(r => setSupportData(prev => ({ ...prev, venues: r.data || [] })))
      .catch(() => {});
  }, []);

  const handleTabClick = (t) => {
    if (t.link) {
      navigate(t.link);
      return;
    }
    setActiveTab(t.id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', t.id);
    setSearchParams(next, { replace: true });
  };

  const visibleTabs = TABS.filter(t => t.id !== 'users' || currentUser?.role === 'super');
  const isSuper = currentUser?.role === 'super';

  return (
    <div className="space-y-6 fade-in">
      <div>
        <h1 className="text-4xl font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2D4A3E' }}>
          People &amp; Places
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage users, key contacts, skating organisations and venues
        </p>
      </div>

      {/* Global country filter — applies to Key Contacts, Skating
          Organisations and Venues (User Administration is un-filtered). */}
      <div className="flex items-center" data-testid="pp-country-row">
        <CountryFilter value={country} onChange={setCountry} testId="pp-country-filter" />
      </div>

      <div className="border-b border-stone-200">
        <div className="flex gap-1 overflow-x-auto">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id && !tab.link;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                data-testid={`pp-tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-green-700 text-green-800 font-medium'
                    : 'border-transparent text-muted-foreground hover:text-green-700 hover:border-green-300'
                }`}
                style={isActive ? { color: '#2D4A3E' } : {}}
                title={tab.link ? 'Open Skaters & Entries manager' : undefined}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        {activeTab === 'users' && isSuper && <UsersAdminPage embedded />}
        {activeTab === 'contacts' && (
          <KeyContactsTab embedded currentUser={currentUser} clubs={supportData.clubs} venues={supportData.venues} country={country} />
        )}
        {activeTab === 'orgs' && <ClubsPage embedded country={country} autoOpenCreate={autoOpenCreate && activeTab === 'orgs'} onAutoOpenConsumed={consumeAutoOpen} />}
        {activeTab === 'venues' && <VenuesPage embedded country={country} autoOpenCreate={autoOpenCreate && activeTab === 'venues'} onAutoOpenConsumed={consumeAutoOpen} />}
      </div>
    </div>
  );
}
